import React from 'react';
import PropTypes from 'prop-types';

import sessionReader from '../../../../store/readHelpers/SessionReader';
import settingsReader from '../../../../store/readHelpers/SettingsReader';

import UploadViewInitial from './UploadViewInitial';
import UploadViewFileUploading from './UploadViewFileUploading';
import UploadViewFileUploaded from './UploadViewFileUploaded';
import UploadViewError from './UploadViewError';

import styles from './uploadInteraction.scss';

const errorTypes = {
    FILE_EXTENSION_NOT_ALLOWED: 'FILE_EXTENSION_NOT_ALLOWED',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    UPLOAD_ERROR: 'UPLOAD_ERROR',
};

const processSteps = {
    INITIAL: 'INITIAL',
    FILE_SELECTION: 'FILE_SELECTION',
    FILE_UPLOADING: 'FILE_UPLOADING',
    FILE_UPLOADED: 'FILE_UPLOADED',
    FILE_NOT_ACCEPTED: 'FILE_NOT_ACCEPTED',
    ERROR: 'ERROR',
};

const defaultState = {
    callbackMessages: [],
    currentStep: processSteps.INITIAL,
    isInitialRender: true,
    error: undefined,
    fileInfo: undefined,
    savedFileContentItemId: undefined,
    selectedFile: undefined,
    uploadProgress: 0,
    uploadProgressTime: undefined,
    uploadProgressEstimatedMsRemaining: undefined,
    uploadStartedAt: undefined,
    uploadCompletedAt: undefined,
    uploadedFileS3Name: undefined,
};

class UploadInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        context: PropTypes.string.isRequired, // The context in which the question is displayed. Eg: 'exam' or 'preview'.
        extraResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isCurrent: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        questionModel: PropTypes.object.isRequired, // model of the question/assessmentItem holding this interaction.
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        direction: PropTypes.string,
    };

    constructor(props, context) {
        super(props, context);

        this.previousView = undefined;

        const { extraResponses, userResponse } = props;
        this.state = this.getStateForUserResponse(userResponse, extraResponses);
    }

    componentDidMount() {
        this.previousView = this.state.currentStep;
        this.props.handleInteractionReady(true);
    }

    componentDidUpdate(prevProps) {
        this.previousView = this.state.currentStep;
        const { extraResponses, userResponse } = this.props;
        if (prevProps.userResponse?.length === 0 && userResponse?.length && extraResponses
            && ![processSteps.FILE_UPLOADING, processSteps.FILE_NOT_ACCEPTED, processSteps.ERROR].includes(this.state.currentStep)) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(this.getStateForUserResponse(userResponse, extraResponses));
        }
    }

    componentWillUnmount() {
        if (this.uploadController) {
            this.uploadController.stopListening();
        }
    }

    getStateForUserResponse = (userResponse, extraResponses) => {
        const { uploadService } = this.props.services;
        if (userResponse && userResponse.length > 0) {
            const newState = Object.assign({}, defaultState);
            const uploadCompletedAt = this.getExtraResponseValue(extraResponses, 'fileClientUploadTime');
            newState.currentStep = processSteps.FILE_UPLOADED;
            newState.fileInfo = {
                fileName: this.getExtraResponseValue(extraResponses, 'fileName'),
                fileType: this.getExtraResponseValue(extraResponses, 'fileType'),
                fileSize: this.getExtraResponseValue(extraResponses, 'fileSize'),
            };
            newState.uploadCompletedAt = new Date(uploadCompletedAt);
            newState.fileDownloadUrl = uploadService.getFileDownloadUrl(userResponse[0]);
            return newState;
        }

        return defaultState;
    };

    getExtraResponseValue = (extraResponses, key) => {
        const response = extraResponses.find((extraResponse) => {
            return {}.hasOwnProperty.call(extraResponse, key); // Done this way because of eslint no-prototype-builtins
        });

        if (response && response[key].length > 0) {
            return response[key][0];
        }

        return undefined;
    };

    cancelUpload = () => {
        this.sendStatusUpdate(false);

        if (this.uploadController) {
            this.uploadController.cancel();
        }
    };

    uploadFile = (fileToUpload) => {
        this.uploadController = this.props.services.uploadService.upload(
            fileToUpload,
            this.props.questionModel.contentItemId,
            this.props.questionModel.contentRevisionId,
            (type, file, payload, id) => {
                this.handleCallback(type, file, payload, id);
            }
        );
    };

    handleCallback = (type, file, payload, id) => {
        const callbackMessages = this.state.callbackMessages.concat({ type, file, payload, id });
        this.setState({ callbackMessages });

        if (type === 'progress') {
            this.handleProgress(payload);
        } else if (type === 'complete') {
            this.setState({ uploadedFileS3Name: payload });
        } else if (type === 'sent-upload-success') {
            this.handleFileUploaded(file, payload);
        } else if (type === 'cancelled') {
            this.handleCancelled(payload);
        } else if (type === 'error') {
            this.handleError(payload);
        }
    };

    handleError = (error) => {
        this.sendStatusUpdate(false);
        this.setState({ currentStep: processSteps.ERROR, error: { type: errorTypes.UPLOAD_ERROR, text: error } });
    };

    handleCancelled = () => {
        this.setState({ currentStep: processSteps.INITIAL });
    };

    handleProgress = (progress) => {
        const uploadProgressTime = new Date().getTime();
        const uploadProgress = parseFloat(progress);
        const newState = { uploadProgress, uploadProgressTime };
        let uploadProgressEstimatedMsRemaining;

        if (uploadProgress !== 0) {
            uploadProgressEstimatedMsRemaining = this.estimateTimeLeft(
                uploadProgress,
                this.state.uploadStartedAt.getTime(),
                uploadProgressTime
            );
        }

        newState.uploadProgressEstimatedMsRemaining = uploadProgressEstimatedMsRemaining;
        this.setState(newState);
    };

    /**
     * Calculates an estimate of how many milliseconds are left, based on data from the current and previous progressUpdates.
     *
     * @param {number} progress
     * @param {int} startTime in ms
     * @param {int} currentTime in ms
     * @returns {int} The estimated number of remaining milliseconds.
     */
    estimateTimeLeft = (progress, startTime, currentTime) => {
        const msTaken = currentTime - startTime;
        const partsPerMs = progress / msTaken;
        const remainingMs = Math.ceil((1 - progress) / partsPerMs);

        return remainingMs;
    };

    handleFileUploaded = (file, payload) => {
        const { identifier } = this.props.model;
        const { uploadService } = this.props.services;
        const time = new Date().toISOString();

        // Store details of the uploaded file
        const extraResponses = [
            {
                identifier: 'fileClientUploadTime',
                cardinality: 'single',
                baseType: 'string',
                candidateResponse: [time],
            },
            {
                identifier: 'fileName',
                cardinality: 'single',
                baseType: 'string',
                candidateResponse: [file.name],
            },
            {
                identifier: 'fileType',
                cardinality: 'single',
                baseType: 'string',
                candidateResponse: [file.type],
            },
            {
                identifier: 'fileSize',
                cardinality: 'single',
                baseType: 'string',
                candidateResponse: [file.size],
            },
        ];


        this.setState({
            currentStep: processSteps.FILE_UPLOADED,
            fileDownloadUrl: uploadService.getFileDownloadUrl(payload.contentItemId),
            uploadCompletedAt: new Date(),
            fileInfo: {
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
            },
        });

        this.props.callback(identifier, [payload.contentItemId], extraResponses);
        this.sendStatusUpdate(false);
    };

    removeUploadedFile = () => {
        const { identifier } = this.props.model;

        // Store the new coordinates of the dragged element
        const extraResponses = [{
            identifier: 'fileClientUploadTime',
            cardinality: 'single',
            baseType: 'string',
            candidateResponse: [],
        }];

        this.setState(defaultState);

        this.props.callback(identifier, [], extraResponses);
    };

    handleFileSelection = (file) => {
        const { name, size, type, lastModified, lastModifiedDate } = file;
        const fileModel = { name, size, type, lastModified, lastModifiedDate };
        const maxFileSize = this.props.services.interactionService.getFileUploadMaxSize();

        if (file.size > maxFileSize) {
            this.setState({
                selectedFile: fileModel,
                currentStep: processSteps.FILE_NOT_ACCEPTED,
                error: { type: errorTypes.FILE_TOO_LARGE, text: '' },
            });
        } else if (!this.verifyFileType(file)) {
            this.setState({
                selectedFile: fileModel,
                currentStep: processSteps.FILE_NOT_ACCEPTED,
                error: { type: errorTypes.FILE_EXTENSION_NOT_ALLOWED, text: '' },
            });
        } else {
            const newState = Object.assign({}, defaultState, {
                selectedFile: fileModel,
                currentStep: processSteps.FILE_UPLOADING,
                uploadStartedAt: new Date(),
            });

            this.setState(newState);

            this.sendStatusUpdate(true);
            this.uploadFile(file);
        }
    };

    handleTryAgain = () => {
        this.setState(defaultState);
    };

    sendStatusUpdate = (uploading) => {
        const { interactionService } = this.props.services;
        interactionService.setFileUploadStatus(uploading, this.props.model.id);
    };

    verifyFileType = (file) => {
        const { supportedFileExtensions } = this.props.model;
        if (supportedFileExtensions === '*') {
            return true;
        } else if (file.name.indexOf('.') === -1) {
            return false;
        }

        let fileExtension = file.name.split('.').pop().toLowerCase();
        fileExtension = `.${fileExtension}`;

        if (fileExtension === '.jpeg' && supportedFileExtensions.indexOf('.jpg') > -1) {
            return true;
        }

        const found = supportedFileExtensions
            .split(',')
            .find(item => item.trim().toLowerCase() === fileExtension);

        return found !== undefined;
    };

    getStatusStyle = (step) => {
        switch (step) {
        case processSteps.INITIAL:
            return styles.statusInitial;

        case processSteps.FILE_SELECTION:
            return styles.statusFileSelection;

        case processSteps.FILE_UPLOADING:
            return styles.statusFileUploading;

        case processSteps.FILE_UPLOADED:
            return styles.statusFileUploaded;

        case processSteps.FILE_NOT_ACCEPTED:
            return styles.statusError;

        case processSteps.ERROR:
            return styles.statusError;

        default:
            return styles.statusInitial;
        }
    };

    getViewClass = (step) => {
        switch (step) {
        case processSteps.FILE_UPLOADING:
            return UploadViewFileUploading;

        case processSteps.FILE_UPLOADED:
            return UploadViewFileUploaded;

        case processSteps.FILE_NOT_ACCEPTED:
            return UploadViewError;

        case processSteps.ERROR:
            return UploadViewError;

        default:
            return UploadViewInitial;
        }
    };

    onDragEnter = (e) => {
        e.preventDefault();
        this.setState({ hovering: true });
    };

    onDragOver = (e) => {
        e.preventDefault();
    };

    onDragEndOrLeave = (e) => {
        e.preventDefault();
        this.setState({ hovering: false });
    };

    onDrop = (e) => {
        e.preventDefault();
        this.setState({ hovering: false });
        if (e.dataTransfer && this.props.context === 'exam') {
            this.handleFileSelection(e.dataTransfer.files[0]);
        }
    };

    shouldHideInteractionsNotUsefulOnPaper() {
        return settingsReader.hasSettingValue(sessionReader.printParamNames.HIDE_INTERACTIONS_NOT_USEFUL_ON_PAPER, true);
    }

    render() {
        const { currentStep } = this.state;
        const { direction } = this.props;
        const statusStyle = this.getStatusStyle(this.state.currentStep);
        const hoveringStyle = this.state.hovering ? styles.dragHovering : '';
        const classNames = `${styles.uploadInteraction} ${statusStyle} ${hoveringStyle} interaction`;

        if (this.shouldHideInteractionsNotUsefulOnPaper()) {
            return null;
        }

        const ViewClass = this.getViewClass(currentStep);
        return (
            <div className={classNames} dir={direction}>
                <ViewClass
                    {...this.props}
                    {...this.state}
                    cancelUpload={this.cancelUpload}
                    handleFileSelection={this.handleFileSelection}
                    handleTryAgain={this.handleTryAgain}
                    previousView={this.previousView}
                    removeUploadedFile={this.removeUploadedFile}
                />
            </div>
        );
    }
}

export default UploadInteraction;
